import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { AxiosError, AxiosResponse } from "axios";

import type {
  GetMentorshipsByMentorRequest,
  GetMentorshipsByStudentRequest,
  InviteStudentRequest,
  SelectMentorRequest,
  SetMentorshipRequest,
  StopMentorshipRequest,
} from "./api.dto";

import type { PaginatedResponse, PaginationRequest } from "@/shared/types";
import { useApi } from "@/shared/api";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import { filterObjectKeys } from "@/shared/lib/utils";
import type { Mentorship } from "../model";

export const useMentorships = (props: PaginationRequest) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<Mentorship>>({
    queryKey: ["getMentorships", params],
    queryFn: async () => {
      const response = await api.get("/mentorships", { params });
      return response.data;
    },
  });
};

export const useStopMentorship = () => {
  const { api } = useApi();
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Наставничество остановлено.",
      },
    },
  });

  return useMutation<AxiosResponse, AxiosError, StopMentorshipRequest>({
    mutationFn: ({ id }) => api.post(`/mentorships/${id}/stop`),
    onSuccess,
    onError,
  });
};

export const useMentorshipsByMentor = (
  props: PaginationRequest & GetMentorshipsByMentorRequest,
) => {
  const { api } = useApi();
  const params = filterObjectKeys(props, ["mentorId"]);

  return useQuery<PaginatedResponse<Mentorship>>({
    queryKey: ["mentorshipsByMentor", props],
    queryFn: async () => {
      const response = await api.get(`/mentorships/mentor/${props.mentorId}`, {
        params,
      });
      return response.data;
    },
  });
};

export const useMentorshipsByStudent = (
  props: PaginationRequest & GetMentorshipsByStudentRequest,
) => {
  const { api } = useApi();
  const params = filterObjectKeys(props, ["studentId"]);

  return useQuery<PaginatedResponse<Mentorship>>({
    queryKey: ["mentorshipsByMentor", params],
    queryFn: async () => {
      const response = await api.get(
        `/mentorships/student/${props.studentId}`,
        {
          params,
        },
      );
      return response.data;
    },
  });
};

export const useSetPercentageRate = () => {
  const { api } = useApi();
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Выставлен новый процент.",
      },
    },
  });

  return useMutation<AxiosResponse, AxiosError, StopMentorshipRequest>({
    mutationFn: (id) => api.delete(`/mentorships/${id}/percentage-rate`),
    onSuccess,
    onError,
  });
};

export const useSetMentorship = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Наставничество изменено.",
      },
    },
  });
  const { api } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse, AxiosError, SetMentorshipRequest>({
    mutationFn: ({ id, percentageRate, status }) =>
      api.patch(`/mentorships/${id}`, { percentageRate, status }),
    onSuccess: () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ["mentorshipsByMentor"] });
    },
    onError,
  });
};

export const useInviteStudent = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Ученик приглашен.",
      },
      error: {
        description: "Произошла ошибка.",
      },
    },
  });
  const { api } = useApi();

  return useMutation<AxiosResponse, AxiosError, InviteStudentRequest>({
    mutationFn: (data) => api.post("/mentorships/invite", data),
    onSuccess,
    onError,
  });
};

export const useSelectMentor = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Наставник выбран.",
      },
    },
  });
  const { api } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse, AxiosError, SelectMentorRequest>({
    mutationFn: (data) => api.post("/mentorships", data),
    onSuccess: () => {
      onSuccess();
      queryClient.refetchQueries({ queryKey: ["mentorshipsByMentor"] });
    },
    onError,
  });
};
